import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Laptop, Smartphone, Trash2 } from 'lucide-react';
import {
  type Device,
  type DevicePlatform,
  listDevices,
  registerDevice,
  unregisterDevice,
} from '@/api/mobileDevices';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

const PLATFORMS: DevicePlatform[] = ['ios', 'android', 'web', 'desktop'];

function fmt(iso: string, locale: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(locale);
}

export function MyDevicesPage() {
  const { t, i18n } = useTranslation();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState<DevicePlatform>('ios');
  const [pushToken, setPushToken] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listDevices()
      .then(setDevices)
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function register() {
    if (!pushToken.trim()) return;
    setBusy(true);
    try {
      await registerDevice({ platform, pushToken: pushToken.trim(), appVersion: appVersion.trim() || undefined });
      setPushToken('');
      setAppVersion('');
      load();
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await unregisterDevice(id);
    load();
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
          {t('devices.title')}
        </h1>
        <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('devices.subtitle')}</p>
      </header>

      {/* Register */}
      <div className="space-y-3 rounded-lg border bg-card p-4" data-testid="devices-register">
        <h2 className="text-sm font-medium text-[var(--color-lop-text-primary)]">
          {t('devices.register')}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('devices.platform')}
            </label>
            <Select value={platform} onChange={(e) => setPlatform(e.target.value as DevicePlatform)}>
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {t(`devices.platformName.${p}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('devices.appVersion')}
            </label>
            <Input value={appVersion} onChange={(e) => setAppVersion(e.target.value)} placeholder="1.0.0" />
          </div>
          <div className="sm:col-span-3">
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('devices.pushToken')}
            </label>
            <Input
              value={pushToken}
              onChange={(e) => setPushToken(e.target.value)}
              placeholder={t('devices.pushTokenPlaceholder')}
              data-testid="devices-token"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={register} disabled={busy || !pushToken.trim()} data-testid="devices-submit">
            {t('devices.registerBtn')}
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-lg border bg-card">
        {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!loading && devices.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('devices.empty')}</p>
        )}
        <ul className="divide-y divide-[var(--color-lop-border)]">
          {devices.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-4 py-3" data-testid="devices-row">
              <span className="text-[var(--color-lop-text-muted)]">
                {d.platform === 'desktop' || d.platform === 'web' ? (
                  <Laptop className="h-5 w-5" />
                ) : (
                  <Smartphone className="h-5 w-5" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[var(--color-lop-text-primary)]">
                  {t(`devices.platformName.${d.platform}`)}
                  {d.appVersion && (
                    <span className="ml-2 text-xs text-[var(--color-lop-text-muted)]">v{d.appVersion}</span>
                  )}
                </p>
                <p className="text-xs text-[var(--color-lop-text-muted)]">
                  {t('devices.lastSeen', { at: fmt(d.lastSeenAt, i18n.language) })}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => revoke(d.id)} aria-label={t('devices.revoke')}>
                <Trash2 className="h-4 w-4 text-[var(--color-lop-danger)]" />
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
